import React, { useEffect, useMemo } from "react";
import { Modal, Tabs, Table, Button, Space, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  closeLinkedMediaModal,
  setLinkedTab,
  setLinkedPage,
  setLinkedPageSize,
  setUnlinkedPage,
  setUnlinkedPageSize,
  fetchLinkedMedia,
  fetchUnlinkedMedia,
  linkMedia,
  unlinkMedia,
} from "./descriptors.slice";
import { getUserRole } from "../../../hooks/getUserRoles";

const LinkedMediaModal: React.FC = () => {
  const userRole = getUserRole();
  const dispatch = useAppDispatch();
  const { modals } = useAppSelector((s) => s.descriptors);
  const m = modals.linkedMedia;

  // Fetch sa herë hapet modali ose ndryshon tab/page
  useEffect(() => {
    if (!m.open || !m.projectId) return;
    if (m.tab === "linked" && m.descriptorId) dispatch(fetchLinkedMedia());
    if (m.tab === "unlinked") dispatch(fetchUnlinkedMedia());
  }, [
    m.open,
    m.projectId,
    m.descriptorId,
    m.tab,
    m.linked.page,
    m.linked.pageSize,
    m.unlinked.page,
    m.unlinked.pageSize,
    dispatch,
  ]);

  // Kolonat e plota si në dizajn
  const commonCols: ColumnsType<any> = useMemo(
    () => [
      {
        title: "Username",
        dataIndex: "username",
        key: "username",
        width: 140,
        ellipsis: true,
      },
      {
        title: "Document name",
        dataIndex: "documentName",
        key: "documentName",
        ellipsis: true,
      },
      {
        title: "Document type",
        dataIndex: "documentType",
        key: "documentType",
        width: 150,
        render: (v: "Internal" | "External" | string) =>
          v ? (
            <Tag
              color={
                v === "Internal"
                  ? "blue"
                  : v === "External"
                  ? "green"
                  : undefined
              }
            >
              {v}
            </Tag>
          ) : null,
      },
      { title: "Codes", dataIndex: "codes", key: "codes", width: 90 },
      { title: "Date", dataIndex: "date", key: "date", width: 120 },
      {
        title: "Highlight length",
        dataIndex: "highlightLength",
        key: "highlightLength",
        width: 150,
      },
      {
        title: "Created by",
        dataIndex: "createdBy",
        key: "createdBy",
        width: 160,
        ellipsis: true,
      },
      {
        title: "Modified by",
        dataIndex: "modifiedBy",
        key: "modifiedBy",
        width: 160,
        ellipsis: true,
      },
    ],
    []
  );

  const linkedCols: ColumnsType<any> = useMemo(
    () => [
      ...commonCols,
      {
        title: "",
        key: "act",
        width: 110,
        fixed: "right",
        align: "right" as const,
        // render: (_: any, row: any) => (
        //   <Button
        //     size="small"
        //     onClick={() => dispatch(unlinkMedia(row.id))}
        //     loading={m.mutatingId === row.id}
        //     disabled={m.mutatingId === row.id}
        //   >
        //     Unlink
        //   </Button>
        // ),
        render: (_: any, row: any) => (
          <>
            {userRole !== "Technical Expert" && (
              <Button
                size="small"
                onClick={() =>
                  row.linkedMediaId && dispatch(unlinkMedia(row.linkedMediaId))
                }
                loading={m.mutatingId === row.linkedMediaId}
                disabled={
                  !row.linkedMediaId || m.mutatingId === row.linkedMediaId
                }
                style={{
                  background: "#A072EB",
                  border: "1px solid #A072EB",
                  color: "white",
                }}
              >
                Unlink
              </Button>
            )}
          </>
        ),
      },
    ],
    [commonCols, dispatch, m.mutatingId]
  );

  const unlinkedCols: ColumnsType<any> = useMemo(
    () => [
      ...commonCols,
      {
        title: "",
        key: "act",
        width: 110,
        fixed: "right",
        align: "right" as const,
        render: (_: any, row: any) => (
          <>
            {userRole !== "Technical Expert" && (
              <Button
                size="small"
                onClick={() => dispatch(linkMedia(row.id))}
                loading={m.mutatingId === row.id}
                disabled={m.mutatingId === row.id}
                style={{
                  background: "#A072EB",
                  border: "1px solid #A072EB",
                  color: "white",
                }}
              >
                Link
              </Button>
            )}
          </>
        ),
      },
    ],
    [commonCols, dispatch, m.mutatingId]
  );

  return (
    <Modal
      open={m.open}
      title={
        <Space direction="vertical" size={0}>
          <span>Linked Media</span>
          {m.descriptorLabel ? (
            <small style={{ opacity: 0.7 }}>{m.descriptorLabel}</small>
          ) : null}
        </Space>
      }
      onCancel={() => dispatch(closeLinkedMediaModal())}
      footer={null}
      width={1200}
      destroyOnClose
      className="linked-unlinked-media-modal"
      style={{ textAlign: "center" }}
    >
      <Tabs
        activeKey={m.tab}
        onChange={(k) => dispatch(setLinkedTab(k as "linked" | "unlinked"))}
        items={[
          {
            key: "linked",
            label: "Link",
            children: (
              <Table
                rowKey={(r) => r.id}
                columns={linkedCols}
                dataSource={m.linked.items}
                loading={m.linked.loading}
                scroll={{ x: 1300 }}
                pagination={{
                  current: m.linked.page,
                  pageSize: m.linked.pageSize,
                  total: m.linked.total,
                  onChange: (p, ps) => {
                    dispatch(setLinkedPage(p));
                    dispatch(setLinkedPageSize(ps));
                  },
                }}
              />
            ),
          },
          {
            key: "unlinked",
            label: "Unlinked",
            children: (
              <Table
                rowKey={(r) => r.id}
                columns={unlinkedCols}
                dataSource={m.unlinked.items}
                loading={m.unlinked.loading}
                scroll={{ x: 1300 }}
                pagination={{
                  current: m.unlinked.page,
                  pageSize: m.unlinked.pageSize,
                  total: m.unlinked.total,
                  onChange: (p, ps) => {
                    dispatch(setUnlinkedPage(p));
                    dispatch(setUnlinkedPageSize(ps));
                  },
                }}
              />
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default LinkedMediaModal;
